import React, { useRef, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

import ShippingIcon from "@/assets/features/shippingActive.png";
import PackageCover from '@/assets/images/packageCover.png';
import ChatCover from '@/assets/images/chatCoverEmerald.png';

gsap.registerPlugin(ScrollTrigger);

const ShippingProcess: React.FC = () => {
  const sectionRef = useRef<HTMLElement>(null);
  const card1Ref = useRef<HTMLDivElement>(null);
  const card2Ref = useRef<HTMLDivElement>(null);
  const card3Ref = useRef<HTMLDivElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);


  const [inView1, setInView1] = useState(false);
  const [inView2, setInView2] = useState(false);
  const [inView3, setInView3] = useState(false);
  const [showGradient, setShowGradient] = useState(false);

  // gradient-on-scroll
  useEffect(() => {
    const onScroll = () => {
      if (!sectionRef.current) return;
      const rect = sectionRef.current.getBoundingClientRect();
      const progress = Math.min(
        1,
        Math.max(0, (window.innerHeight - rect.top) / (rect.height - window.innerHeight))
      );
      setShowGradient(progress > 0.95);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // slide-in / slide-out cards
  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.target === card1Ref.current) {
            if (entry.isIntersecting) setInView1(true);
            else if (entry.boundingClientRect.top > 0) setInView1(false);
          }
          if (entry.target === card2Ref.current) {
            if (entry.isIntersecting) setInView2(true);
            else if (entry.boundingClientRect.top > 0) setInView2(false);
          }
          if (entry.target === card3Ref.current) {
            if (entry.isIntersecting) setInView3(true);
            else if (entry.boundingClientRect.top > 0) setInView3(false);
          }
        });
      },
      { threshold: 0.5 }
    );

    [card1Ref, card2Ref, card3Ref].forEach(ref => {
      if (ref.current) observer.observe(ref.current);
    });
    return () => observer.disconnect();
  }, []);

  // image parallax on scroll (desktop only)
  useEffect(() => {
  if (!imageRef.current || !sectionRef.current) return;

  const image = imageRef.current;

  const tl = gsap.timeline({
    scrollTrigger: {
      trigger: sectionRef.current,
      start: 'top bottom',
      end: 'bottom top',
      scrub: true,
      onUpdate: (self) => {
        const progress = self.progress;

        const shakeX = gsap.utils.random(-25, 25) * progress;
        const shakeY = gsap.utils.random(-25, 25) * progress;
        const rotate = gsap.utils.random(-10, 10) * progress;
        const scale = 1.2 + gsap.utils.random(-0.05, 0.05) * progress;

        gsap.to(image, {
          x: shakeX,
          y: -150 + shakeY,
          rotation: rotate,
          scale: scale,
          duration: 0.05,
          ease: 'none',
          overwrite: 'auto',
        });
      },
    },
  });

  return () => {
    tl.scrollTrigger?.kill();
    tl.kill();
  };
}, []);




  return (
    <section
      ref={sectionRef}
      className="
        tw-relative tw-w-full 
        lg:tw-h-[350vh] lg:tw-overflow-visible 
        sm:tw-h-auto sm:tw-overflow-hidden
      "
    >
      {/* Pure black */}
      <div className="tw-absolute tw-inset-0 tw-bg-black" />

      {/* Gradient layer */}
      <div
        className={`
          tw-absolute tw-inset-0
          tw-bg-gradient-to-b tw-from-black tw-via-black tw-to-[#04f2b4]
          tw-transition-opacity tw-duration-700 tw-rounded-b-xl tw-mx-10
          ${showGradient ? 'tw-opacity-100' : 'tw-opacity-0'}
        `}
      />

      {/* Cards container for SM/MD */}
      <div className="tw-flex tw-flex-col tw-space-y-12 tw-items-center tw-py-16 lg:tw-block">
        {/* Step 1 */}
        <div className="lg:tw-absolute lg:tw-top-[10%] lg:tw-left-[10%]">
          <motion.div
            ref={card1Ref}
            initial={{ opacity: 0, x: -100 }}
            animate={inView1 ? { opacity: 1, x: 0 } : { opacity: 0, x: -100 }}
            transition={{ duration: 0.6 }}
            className="tw-bg-[#121212] tw-rounded-xl tw-p-6 md:tw-p-8 tw-w-80 md:tw-w-80 tw-z-20 tw-relative">
            <span className="tw-text-[#04f2b4] tw-text-9xl tw-font-bold">1</span>
            <div className="tw-mt-2">
              <p className="tw-text-[#04f2b4] tw-text-2xl tw-font-semibold tw-uppercase tw-tracking-widest">
                Protection
              </p>
              <p className="tw-text-[#04f2b4] tw-text-2xl tw-font-semibold tw-uppercase tw-tracking-widest">
                Purchased
              </p>
            </div>
            <p className="tw-text-white tw-text-4xl tw-leading-tight tw-mt-4">
              Customer purchases shipping protection.
            </p>
          </motion.div>

          {/* Discarded package image (desktop only) */}
          <img
            ref={imageRef}
            src={PackageCover}
            alt="Discarded Package"
            className="tw-hidden lg:tw-block tw-absolute tw-bottom-[-550px] tw-left-50 tw-w-[480px] tw-scale-150 tw-z-0 tw-pointer-events-none"
          />
        </div>

        {/* Step 2 */}
        <div className='tw-overflow-x-hidden'>
        <motion.div
          ref={card2Ref}
          initial={{ opacity: 0, x: 100 }}
          animate={inView2 ? { opacity: 1, x: 0 } : { opacity: 0, x: 100 }}
          transition={{ duration: 0.6 }}
          className="lg:tw-absolute lg:tw-top-[28%] lg:tw-right-[10%] tw-bg-[#121212] tw-rounded-xl tw-p-6 md:tw-p-8 tw-w-80 md:tw-w-80 tw-z-20 tw-relative">
          <span className="tw-text-[#04f2b4] tw-text-9xl tw-font-bold">2</span>
          <div className="tw-mt-2">
            <p className="tw-text-[#04f2b4] tw-text-2xl tw-font-semibold tw-uppercase tw-tracking-widest">
              Doomed
            </p>
            <p className="tw-text-[#04f2b4] tw-text-2xl tw-font-semibold tw-uppercase tw-tracking-widest">
              Delivery
            </p>
          </div>
          <p className="tw-text-white tw-text-4xl tw-leading-tight tw-mt-4">
            Loss, theft, or damage occurs.
          </p>
        </motion.div>
        </div>

        {/* Step 3 */}
        <motion.div
          ref={card3Ref}
          initial={{ opacity: 0, x: -100 }}
          animate={inView3 ? { opacity: 1, x: 0 } : { opacity: 0, x: -100 }}
          transition={{ duration: 0.6 }}
          className="lg:tw-absolute lg:tw-top-[48%] lg:tw-left-[10%] tw-bg-[#121212] tw-rounded-xl tw-p-6 md:tw-p-8 tw-w-80 md:tw-w-80 tw-z-20 tw-relative"
        >
          <span className="tw-text-[#04f2b4] tw-text-9xl tw-font-bold">3</span>
          <div className="tw-mt-2">
            <p className="tw-text-[#04f2b4] tw-text-2xl tw-font-semibold tw-uppercase tw-tracking-widest">
              Simple
            </p>
            <p className="tw-text-[#04f2b4] tw-text-2xl tw-font-semibold tw-uppercase tw-tracking-widest">
              Resolution
            </p>
          </div>
          <p className="tw-text-white tw-text-4xl tw-leading-tight tw-mt-4">
            No-hassle claims and simple replacements.
          </p>
        </motion.div>
      </div>

      {/* Chat UI (desktop only) */}
      <div className="tw-hidden lg:tw-block">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8 }}
          className="tw-absolute tw-top-[52%] tw-right-[5%] tw-w-[380px] tw-z-0"
        >
          <img
            src={ChatCover}
            alt="Chat UI"
            className="tw-block tw-w-full tw-pointer-events-none"
          />
        </motion.div>
      </div>

      {/* Responsive CTA Section */}
      <div className="tw-w-5/6 tw-px-12 lg:tw-absolute lg:tw-bottom-0 lg:tw-mx-20 tw-bg-transparent tw-relative tw-z-30">
        <div className="tw-w-full tw-py-16 sm:tw-py-20">
          <div className="tw-max-w-3xl tw-mx-auto tw-px-4 sm:tw-px-6 lg:tw-px-0 tw-text-left lg:tw-text-left">
            {/* Logo + label */}
            <div className="tw-flex tw-items-center tw-mb-6">
              <img src={ShippingIcon} alt="Protega Logo" className="tw-h-8" />
              <div className="tw-flex tw-flex-col tw-ml-2">
                <div className="tw-flex tw-items-center tw-text-lg tw-font-medium tw-text-black tw-gap-0.5">
                  <span>PROT</span>
                  <div className="tw-flex tw-flex-col tw-gap-[2px] tw-h-[1em] tw-justify-center">
                    <span className="tw-w-3 tw-h-[2px] tw-bg-black"></span>
                    <span className="tw-w-3 tw-h-[2px] tw-bg-black"></span>
                    <span className="tw-w-3 tw-h-[2px] tw-bg-black"></span>
                  </div>
                  <span>GA</span>
                </div>
                <span className="tw-text-lg tw-font-bold tw-text-black">Shipping Protection</span>
              </div>
            </div>

            {/* Headline */}
            <h2 className="tw-text-3xl sm:tw-text-4xl md:tw-text-5xl lg:tw-text-6xl tw-font-extrabold tw-text-black tw-leading-tight tw-mb-8">
              Stop paying to fix doomed deliveries.
            </h2>

            {/* Button */}
            <Link
              to="/schedule-demo"
              className="tw-inline-block tw-w-full tw-text-center sm:tw-w-auto tw-font-semibold tw-py-3 tw-px-6 tw-rounded-full tw-bg-[#111111] tw-text-white hover:tw-bg-white hover:tw-text-black tw-transition-all tw-duration-700"
            >
              Schedule a Demo
            </Link>
          </div>
        </div>
      </div>

    </section>
  );
};

export default ShippingProcess;
